import React from 'react';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar,
} from 'recharts';
import { analyticsService, authService } from '../../services';
import { PRODUCTS, RangeValue } from '../../constants/product.constant';
import StatCard from '../../components/analytics/StatCard';
import ChartCard from '../../components/analytics/ChartCard';
import { Loadable, useAnalyticsData } from '../../components/analytics/tabHelpers';
import { fmtNum, fmtPct, CHART_COLORS } from '../../components/analytics/format';

const OverviewTab: React.FC<{ days: RangeValue }> = ({ days }) => {
  const { loading, error, data } = useAnalyticsData<any>(() => analyticsService.getOverview(days), [days]);

  const perDay = (data?.perDay || []).map((d: any) => ({ date: d._id, signups: d.count }));
  const byType = (data?.byUserType || []).map((d: any) => ({ type: d._id, count: d.count }));

  // Full-access admins see the platform-wide total; restricted admins see only
  // their products. Products visible to this admin (for cards + table).
  const fullAccess = authService.isFullAccess;
  const visibleProducts = PRODUCTS.filter((p) => authService.hasProduct(p.key));
  const usage = data?.productUsage || {};

  return (
    <Loadable loading={loading} error={error}>
      <Grid container spacing={3}>
        {fullAccess ? (
          <Grid item xs={12} sm={6} md={3}>
            <StatCard label="Total users" value={fmtNum(data?.totalUsers)} sub="Excludes mocked accounts" />
          </Grid>
        ) : (
          visibleProducts.map((p) => (
            <Grid item xs={12} sm={6} md={3} key={p.key}>
              <StatCard label={`${p.label} users`} value={fmtNum(usage[p.key]?.everTouched)} sub="Ever touched" />
            </Grid>
          ))
        )}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Signups (window)" value={fmtNum(data?.signupsInWindow)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="Mobile-verified" value={fmtNum(data?.mobileVerifiedInWindow)} sub={`${fmtPct(data?.mobileVerifiedRate)} of new signups`} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard label="User types" value={fmtNum(byType.length)} sub="Distinct roles in window" />
        </Grid>

        <Grid item xs={12} md={8}>
          <ChartCard title="Signups per day">
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={perDay} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="signups" stroke={CHART_COLORS[0]} strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
        <Grid item xs={12} md={4}>
          <ChartCard title="Signups by user type">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byType} margin={{ top: 8, right: 16, bottom: 8, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="type" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill={CHART_COLORS[1]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        <Grid item xs={12}>
          <ChartCard title="Product usage">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell align="right">Ever touched</TableCell>
                  <TableCell align="right">Active (window)</TableCell>
                  <TableCell align="right">New (window)</TableCell>
                  <TableCell align="right">Onboarded</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleProducts.map((p) => {
                  const u = (data?.productUsage || {})[p.key] || {};
                  return (
                    <TableRow key={p.key}>
                      <TableCell><Typography variant="body2">{p.label}</Typography></TableCell>
                      <TableCell align="right">{fmtNum(u.everTouched)}</TableCell>
                      <TableCell align="right">{fmtNum(u.activeInWindow)}</TableCell>
                      <TableCell align="right">{fmtNum(u.newInWindow)}</TableCell>
                      <TableCell align="right">{fmtNum(u.onboarded)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </ChartCard>
        </Grid>
      </Grid>
    </Loadable>
  );
};

export default OverviewTab;
